import {
  createCreationFieldInitialValue,
  type CreateFieldOptions,
  type CreateTargetStatusOptions,
  type CreationValues,
} from '@cims-online/shared'
import { Alert, Form, Select } from 'antd'
import PayItemSelectProvider, {
  usePayItemSelectContext,
} from 'components/CIMSSelect/common/PayItemSelect/PayItemSelectContext'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  CreateWorkflowRecordFormValues,
  validateCreationValues,
} from '../../validation'
import CreationField from '../CreationField'

export const CREATE_WORKFLOW_RECORD_FORM_ID = 'create-workflow-record-form'

type CreateWorkflowRecordFormProps = {
  onSubmit: (
    values: CreationValues,
    targetStatus: CreateTargetStatusOptions['targetStatus']
  ) => Promise<void>
  onTargetChange: (targetStatus: CreateTargetStatusOptions) => void
  onValidityChange: (valid: boolean) => void
  submitting: boolean
  targetStatuses: CreateTargetStatusOptions[]
}

function targetStatusKey({ targetStatus }: CreateTargetStatusOptions): string {
  return targetStatus.type === 'draft'
    ? 'draft'
    : `status:${targetStatus.statusId}`
}

function compareFields(
  first: CreateFieldOptions,
  second: CreateFieldOptions
): number {
  const firstRequired = first.required !== false
  const secondRequired = second.required !== false
  if (firstRequired !== secondRequired) return firstRequired ? -1 : 1

  const nameComparison = first.name.localeCompare(second.name)
  return nameComparison || first.id.localeCompare(second.id)
}

/** Whether initialization needs the asynchronous project pay-item options. */
function waitsForPayItemOptions(field: CreateFieldOptions): boolean {
  return (
    field.type === 'pay_items' &&
    field.initialValueTransformers.some(
      (transformer) => transformer.enabled && transformer.value === 'all'
    )
  )
}

/** Owns target selection, initialized values, and shared-schema validation. */
function CreateWorkflowRecordFormContent({
  onSubmit,
  onTargetChange,
  onValidityChange,
  submitting,
  targetStatuses,
}: CreateWorkflowRecordFormProps) {
  const { loading: payItemsLoading, payItems } = usePayItemSelectContext()
  const activePayItemIds = useMemo(
    () =>
      payItems
        .filter(({ deleted }) => !deleted)
        .map(({ globalId }) => globalId),
    [payItems]
  )
  const [form] = Form.useForm<CreateWorkflowRecordFormValues>()
  const valuesByTarget = useRef<Record<string, CreateWorkflowRecordFormValues>>(
    {}
  )
  const [selectedTargetKey, setSelectedTargetKey] = useState(() =>
    targetStatusKey(targetStatuses[0])
  )
  const selectedTarget =
    targetStatuses.find(
      (targetStatus) => targetStatusKey(targetStatus) === selectedTargetKey
    ) ?? targetStatuses[0]
  const fields = [...selectedTarget.fields].sort(compareFields)
  const [validation, setValidation] = useState(() =>
    validateCreationValues(fields, {})
  )
  const [visibleFieldIssues, setVisibleFieldIssues] = useState(
    () => new Set<string>()
  )
  const [showFormIssues, setShowFormIssues] = useState(false)

  const validate = useCallback(
    (values: CreateWorkflowRecordFormValues) => {
      const result = validateCreationValues(selectedTarget.fields, values)
      setValidation(result)
      onValidityChange(result.valid)
      return result
    },
    [onValidityChange, selectedTarget.fields]
  )

  useEffect(() => {
    const resolvedKey = targetStatusKey(selectedTarget)
    if (resolvedKey !== selectedTargetKey) setSelectedTargetKey(resolvedKey)
    onTargetChange(selectedTarget)

    const targetValues = {
      ...(valuesByTarget.current[resolvedKey] ?? {}),
    }
    for (const field of selectedTarget.fields) {
      if (Object.prototype.hasOwnProperty.call(targetValues, field.id)) continue
      if (payItemsLoading && waitsForPayItemOptions(field)) continue
      targetValues[field.id] = createCreationFieldInitialValue(
        field,
        activePayItemIds
      )
    }
    valuesByTarget.current[resolvedKey] = targetValues
    form.setFields(
      selectedTarget.fields.map(({ id }) => ({
        name: id,
        value: targetValues[id],
      }))
    )
    validate(targetValues)
  }, [
    activePayItemIds,
    form,
    onTargetChange,
    payItemsLoading,
    selectedTarget,
    selectedTargetKey,
    validate,
  ])

  const handleSubmit = useCallback(async () => {
    if (submitting) return

    const result = validate(form.getFieldsValue(true))
    if (!result.valid || !result.data) {
      setVisibleFieldIssues(new Set(Object.keys(result.fieldIssues)))
      setShowFormIssues(true)
      return
    }

    await onSubmit(result.data, selectedTarget.targetStatus)
  }, [form, onSubmit, selectedTarget.targetStatus, submitting, validate])

  return (
    <Form<CreateWorkflowRecordFormValues>
      form={form}
      id={CREATE_WORKFLOW_RECORD_FORM_ID}
      layout="vertical"
      onBlur={() => validate(form.getFieldsValue(true))}
      onFinish={handleSubmit}
      onValuesChange={(_, values) => {
        const targetValues = valuesByTarget.current[selectedTargetKey] ?? {}
        for (const { id } of selectedTarget.fields) {
          if (Object.prototype.hasOwnProperty.call(targetValues, id))
            targetValues[id] = values[id]
        }
        valuesByTarget.current[selectedTargetKey] = targetValues
        validate(values)
      }}
    >
      {targetStatuses.length > 1 && (
        <Form.Item colon label="Send to">
          <Select
            aria-label="Send to"
            onChange={setSelectedTargetKey}
            options={targetStatuses.map((targetStatus) => ({
              label: targetStatus.label,
              value: targetStatusKey(targetStatus),
            }))}
            style={{ width: '100%' }}
            value={targetStatusKey(selectedTarget)}
          />
        </Form.Item>
      )}
      {showFormIssues && validation.formIssues.length > 0 && (
        <Alert
          message={validation.formIssues.join(' ')}
          showIcon
          type="error"
        />
      )}
      {fields.map((field) => (
        <CreationField
          error={
            visibleFieldIssues.has(field.id)
              ? validation.fieldIssues[field.id]?.join(' ')
              : undefined
          }
          field={field}
          key={field.id}
          onBlur={() => {
            setVisibleFieldIssues((visible) => new Set(visible).add(field.id))
            validate(form.getFieldsValue(true))
          }}
        />
      ))}
    </Form>
  )
}

/** Provides project pay-item options to the creation form and its controls. */
function CreateWorkflowRecordForm(props: CreateWorkflowRecordFormProps) {
  return (
    <PayItemSelectProvider>
      <CreateWorkflowRecordFormContent {...props} />
    </PayItemSelectProvider>
  )
}

export default CreateWorkflowRecordForm
